import React, { useEffect, useRef, useState } from 'react';
import { IDSAlert, IDSButton, IDSButtonGroup, IDSSpinner, IDSTextarea } from '@inera/ids-react';
import GeneralExceptionSuggestion from './GeneralExceptionSuggestion';
import SimilarTicketsSuggestion from './SimilarTicketsSuggestion';
import TkbFieldRulesPanel from './TkbFieldRulesPanel';
import { fetchGeneralExceptionSuggestions } from '../../services/generalExceptionsAPI';
import { fetchSimilarTickets } from '../../services/similarTicketsAPI';
import { fetchRelevantTkbRules } from '../../services/tkbRulesAPI';
import { previewDeviationHandling } from '../../services/testExecutionAPI';

const buildSelectedPerTicketBodyNotes = (selectedIds, perTicketBodyNotes) => {
  const selectedPerTicketBodyNotes = {};
  for (const id of selectedIds) {
    const normalized = perTicketBodyNotes[id]?.trim();
    if (normalized) {
      selectedPerTicketBodyNotes[id] = normalized;
    }
  }
  return selectedPerTicketBodyNotes;
};

const PREVIEW_DEBOUNCE_MS = process.env.NODE_ENV === 'test' ? 0 : 250;

const getTextareaValue = (event) => {
  const detailValue = event?.detail?.value;
  if (typeof detailValue === 'string') {
    return detailValue;
  }

  const targetValue = event?.target?.value;
  return typeof targetValue === 'string' ? targetValue : '';
};

/**
 * Inline card rendered inside the accordion when the reviewer clicks
 * "Påbörja avvikelsehantering". Shows all FAILED test results and lets the
 * reviewer toggle which ones should become Jira deviation tickets.
 * For each result, shows similar tickets and an AI-suggested General Exception.
 */
const DeviationReviewCard = ({
  failedResults,
  selectedIds,
  onToggle,
  onConfirm,
  onCancel,
  isSubmitting,
  error,
  executionId,
}) => {
  const [suggestions, setSuggestions] = useState({});
  const [confirmedExceptions, setConfirmedExceptions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [similarTickets, setSimilarTickets] = useState({});
  const [loadingTickets, setLoadingTickets] = useState({});
  // checkedSimilarTickets: { [resultId]: { [issueKey]: boolean } }
  const [checkedSimilarTickets, setCheckedSimilarTickets] = useState({});
  const [tkbRules, setTkbRules] = useState({});
  const [visibleTkbRules, setVisibleTkbRules] = useState({});
  const [loadingTkb, setLoadingTkb] = useState(false);
  const [sharedBodyNote, setSharedBodyNote] = useState('');
  const [perTicketBodyNotes, setPerTicketBodyNotes] = useState({});
  const [showSharedNoteEditor, setShowSharedNoteEditor] = useState(false);
  const [showPerTicketNoteEditor, setShowPerTicketNoteEditor] = useState({});
  const [previewByResultId, setPreviewByResultId] = useState({});
  const [previewExpanded, setPreviewExpanded] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewRequestSequenceRef = useRef(0);

  const handleSharedNoteInput = (event) => {
    setSharedBodyNote(getTextareaValue(event));
  };

  const handlePerTicketNoteInput = (testResultId, event) => {
    const value = getTextareaValue(event);
    setPerTicketBodyNotes((prev) => ({ ...prev, [testResultId]: value }));
  };

  const handleOpenPerTicketNoteEditor = (testResultId) => {
    setShowPerTicketNoteEditor((prev) => ({ ...prev, [testResultId]: true }));
  };

  useEffect(() => {
    if (!failedResults || failedResults.length === 0) return;
    const ids = failedResults.map(r => r.id);
    setLoadingSuggestions(true);
    fetchGeneralExceptionSuggestions(ids, executionId)
      .then(data => {
        setSuggestions(data);
        const initial = {};
        failedResults.forEach(r => {
          const s = data[r.id];
          if (s && s.length > 0) {
            const keyField = s[0].issueKey !== undefined ? 'issueKey' : 'key';
            initial[r.id] = s[0][keyField];
          }
        });
        setConfirmedExceptions(initial);
      })
      .catch(err => {
        console.warn('AI suggestions unavailable:', err.message);
        setAiError('AI-förslag är inte tillgängliga för tillfället.');
      })
      .finally(() => setLoadingSuggestions(false));
  }, [failedResults, executionId]);

  // Fetch similar tickets individually per result as they become selected
  useEffect(() => {
    if (!failedResults || failedResults.length === 0) return;
    failedResults.forEach(r => {
      if (selectedIds.has(r.id) && similarTickets[r.id] === undefined && !loadingTickets[r.id]) {
        setLoadingTickets(prev => ({ ...prev, [r.id]: true }));
        fetchSimilarTickets(r.id, executionId)
          .then(tickets => setSimilarTickets(prev => ({ ...prev, [r.id]: tickets })))
          .catch(err => {
            console.warn('Similar tickets unavailable for', r.id, ':', err.message);
            setSimilarTickets(prev => ({ ...prev, [r.id]: [] }));
          })
          .finally(() => setLoadingTickets(prev => ({ ...prev, [r.id]: false })));
      }
    });
  }, [selectedIds, failedResults, executionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!failedResults || failedResults.length === 0) return;
    setLoadingTkb(true);
    Promise.all(
      failedResults.map(result =>
        fetchRelevantTkbRules(result.id)
          .then(rules => ({ id: result.id, rules }))
          .catch(() => ({ id: result.id, rules: [] }))
      )
    ).then(entries => {
      const map = {};
      entries.forEach(({ id, rules }) => { if (rules.length > 0) map[id] = rules; });
      setTkbRules(map);
      setVisibleTkbRules(map);
    }).finally(() => setLoadingTkb(false));
  }, [failedResults]);

  useEffect(() => {
    if (!failedResults || failedResults.length === 0) return;
    setPerTicketBodyNotes((prev) => {
      const next = {};
      failedResults.forEach((result) => {
        if (selectedIds.has(result.id) && prev[result.id] !== undefined) {
          next[result.id] = prev[result.id];
        }
      });
      return next;
    });
  }, [selectedIds, failedResults]);

  useEffect(() => {
    if (!failedResults || failedResults.length === 0) return;
    setShowPerTicketNoteEditor((prev) => {
      const next = {};
      failedResults.forEach((result) => {
        if (selectedIds.has(result.id) && prev[result.id]) {
          next[result.id] = true;
        }
      });
      return next;
    });
  }, [selectedIds, failedResults]);

  useEffect(() => {
    if (!failedResults || failedResults.length === 0 || selectedIds.size === 0) {
      setPreviewByResultId({});
      setLoadingPreview(false);
      setPreviewError(null);
      return;
    }

    const selectedResultIds = failedResults.filter((r) => selectedIds.has(r.id)).map((r) => r.id);
    if (selectedResultIds.length === 0) {
      setPreviewByResultId({});
      setLoadingPreview(false);
      setPreviewError(null);
      return;
    }

    const generalExceptionKeys = {};
    const similarTicketKeys = {};
    const overrideTkbRules = {};
    selectedResultIds.forEach((id) => {
      const key = confirmedExceptions[id];
      if (key) generalExceptionKeys[id] = key;
      const checked = checkedSimilarTickets[id] || {};
      const selected = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
      if (selected.length > 0) similarTicketKeys[id] = selected;
      if (visibleTkbRules[id] !== undefined) overrideTkbRules[id] = visibleTkbRules[id];
    });
    const selectedPerTicketBodyNotes = buildSelectedPerTicketBodyNotes(selectedResultIds, perTicketBodyNotes);

    let cancelled = false;
    const requestSequence = previewRequestSequenceRef.current + 1;
    previewRequestSequenceRef.current = requestSequence;
    const runPreviewRequest = () => {
      if (cancelled) return;
      setLoadingPreview(true);
      previewDeviationHandling(
        executionId,
        selectedResultIds,
        generalExceptionKeys,
        similarTicketKeys,
        Object.keys(overrideTkbRules).length > 0 ? overrideTkbRules : undefined,
        sharedBodyNote,
        selectedPerTicketBodyNotes
      )
        .then((response) => {
          if (cancelled || requestSequence !== previewRequestSequenceRef.current) return;
          const items = Array.isArray(response?.items) ? response.items : (Array.isArray(response) ? response : []);
          const next = {};
          items.forEach((item) => {
            const resultId = item.testResultId ?? item.resultId ?? item.id;
            if (resultId !== undefined) next[resultId] = item;
          });
          setPreviewByResultId(next);
          setPreviewError(null);
        })
        .catch((err) => {
          if (cancelled || requestSequence !== previewRequestSequenceRef.current) return;
          console.warn('Preview unavailable:', err.message);
          setPreviewByResultId({});
          setPreviewError('Kunde inte hämta förhandsgranskning. Försök igen genom att justera indata.');
        })
        .finally(() => {
          if (!cancelled && requestSequence === previewRequestSequenceRef.current) {
            setLoadingPreview(false);
          }
        });
    };

    let timeoutId;
    if (PREVIEW_DEBOUNCE_MS === 0) {
      runPreviewRequest();
    } else {
      timeoutId = setTimeout(runPreviewRequest, PREVIEW_DEBOUNCE_MS);
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedIds, failedResults, confirmedExceptions, checkedSimilarTickets, visibleTkbRules, sharedBodyNote, perTicketBodyNotes, executionId]);

  const handleExceptionChange = (testResultId, key) => {
    setConfirmedExceptions(prev => ({ ...prev, [testResultId]: key }));
  };

  const handleSimilarTicketCheck = (testResultId, issueKey, isChecked) => {
    setCheckedSimilarTickets(prev => ({
      ...prev,
      [testResultId]: { ...(prev[testResultId] || {}), [issueKey]: isChecked },
    }));
  };

  const handleTkbDismiss = (testResultId, remaining) => {
    setVisibleTkbRules(prev => ({ ...prev, [testResultId]: remaining }));
  };

  const handleConfirm = () => {
    const keys = {};
    const ticketKeys = {};
    for (const id of selectedIds) {
      const key = confirmedExceptions[id];
      if (key) keys[id] = key;
      const checked = checkedSimilarTickets[id] || {};
      const selected = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
      if (selected.length > 0) ticketKeys[id] = selected;
    }
    const overrideTkbRules = {};
    for (const id of selectedIds) {
      if (visibleTkbRules[id] !== undefined) {
        overrideTkbRules[id] = visibleTkbRules[id];
      }
    }
    const selectedPerTicketBodyNotes = buildSelectedPerTicketBodyNotes(selectedIds, perTicketBodyNotes);
    onConfirm(
      keys,
      ticketKeys,
      Object.keys(overrideTkbRules).length > 0 ? overrideTkbRules : undefined,
      sharedBodyNote,
      selectedPerTicketBodyNotes
    );
  };

  const selectedCount = selectedIds.size;
  const totalCount = failedResults.length;

  if (totalCount === 0) {
    return (
      <IDSAlert headline={<h3>Inga underkända testfall</h3>} type="info">
        <p>Inga underkända testfall hittades för denna körning.</p>
      </IDSAlert>
    );
  }

  return (
    <div className="ids-p-4" style={{ border: '1px solid var(--ids-palette-primary-40)', borderRadius: '4px' }}>
      <div className="ids-flex ids-mb-4" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Identifierade avvikelser</h3>
        <span>
          {totalCount} avvikelse{totalCount !== 1 ? 'r' : ''} – {selectedCount} markerade för Jira
        </span>
      </div>

      <IDSAlert type="info">
        <p>Nedan visas alla underkända testfall. Välj vilka som ska skickas till Jira som avvikelseärenden.</p>
      </IDSAlert>
      <div className="ids-mt-3 ids-p-3" style={{ border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '4px', background: 'var(--ids-palette-neutral-100)' }}>
        <div style={{ fontSize: '12px', color: 'var(--ids-palette-neutral-50)', marginBottom: '6px', fontWeight: 600 }}>
          Kompletterande kommentarer till Jira-beskrivning
        </div>
        {!showSharedNoteEditor ? (
          <div className="ids-flex" style={{ alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <div style={{ fontSize: '13px', color: 'var(--ids-palette-neutral-40)' }}>
              {sharedBodyNote?.trim() ? sharedBodyNote : 'Ingen gemensam kommentar tillagd'}
            </div>
            <IDSButton
              size="s"
              secondary
              title="Lägg till kommentar"
              onClick={() => setShowSharedNoteEditor(true)}
            >
              Redigera gemensam kommentar
            </IDSButton>
          </div>
        ) : (
          <IDSTextarea
            id="deviation-shared-body-note"
            label="Gemensam anteckning för Jira-beskrivning"
            rows={3}
            placeholder="Lägg till en gemensam kommentar som ska synas i alla skapade Jira-ärenden."
            value={sharedBodyNote}
            onInput={handleSharedNoteInput}
            onChange={handleSharedNoteInput}
            style={{ width: '100%', resize: 'vertical' }}
          />
        )}
        <div style={{ fontSize: '12px', color: 'var(--ids-palette-neutral-50)', marginTop: '6px' }}>
          Visas längst ned i varje Jira-beskrivning.
        </div>
      </div>

      {aiError && (
        <IDSAlert type="attention" className="ids-mt-3">
          <p>{aiError}</p>
        </IDSAlert>
      )}

      {error && (
        <IDSAlert type="error" className="ids-mt-3">
          <p>{error}</p>
        </IDSAlert>
      )}

      <div className="ids-mt-4">
        {failedResults.map(result => {
          const isSelected = selectedIds.has(result.id);
          const resultSuggestions = suggestions[result.id] || [];
          const tickets = similarTickets[result.id];
          const ticketsLoading = !!loadingTickets[result.id];
          const selectedSimilarKeys = Object.entries(checkedSimilarTickets[result.id] || {})
            .filter(([, checked]) => checked)
            .map(([issueKey]) => issueKey);
          const selectedGeneralException = confirmedExceptions[result.id];
          const previewItem = previewByResultId[result.id];
          const previewSummary = previewItem?.summary || 'Saknas';
          const previewBody = previewItem?.body || 'Saknas';
          return (
            <div
              key={result.id}
              className="ids-p-3 ids-mb-2"
              style={{
                border: `1px solid ${isSelected ? 'var(--ids-palette-information-95)' : 'var(--ids-palette-error-40)'}`,
                borderRadius: '4px',
                background: isSelected ? 'var(--ids-palette-neutral-100)' : 'var(--ids-palette-error-95)',
                position: 'relative',
              }}
            >
              <div className="ids-flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {result.testSuite && (
                    <div style={{ fontSize: '11px', color: 'var(--ids-palette-neutral-60)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {result.testSuite}
                    </div>
                  )}
                  <div style={{
                    fontWeight: '600',
                    textDecoration: isSelected ? 'none' : 'line-through',
                    color: isSelected ? 'inherit' : 'var(--ids-palette-neutral-70)',
                  }}>
                    {result.testCase}
                  </div>
                  {result.justification && (
                    <div style={{ fontSize: '13px', color: isSelected ? 'var(--ids-palette-neutral-50)' : 'var(--ids-palette-neutral-70)', fontStyle: 'italic' }}>
                      "{result.justification}"
                    </div>
                  )}

                  {isSelected && (
                    <div style={{ marginTop: '8px', borderTop: '1px solid var(--ids-palette-neutral-90)', paddingTop: '8px' }}>
                      <div className="ids-p-2" style={{ border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '4px', background: 'var(--ids-palette-neutral-100)' }}>
                        {!showPerTicketNoteEditor[result.id] ? (
                          <div className="ids-flex" style={{ alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <div style={{ fontSize: '13px', color: 'var(--ids-palette-neutral-40)' }}>
                              {perTicketBodyNotes[result.id]?.trim() ? perTicketBodyNotes[result.id] : 'Ingen ärendespecifik kommentar tillagd'}
                            </div>
                            <IDSButton
                              size="s"
                              secondary
                              aria-label={`Redigera kommentar för ${result.testCase}`}
                              title="Lägg till kommentar"
                              onClick={() => handleOpenPerTicketNoteEditor(result.id)}
                            >
                              Redigera kommentar
                            </IDSButton>
                          </div>
                        ) : (
                          <IDSTextarea
                            id={`deviation-note-${result.id}`}
                            label={`Anteckning för ${result.testCase}`}
                            rows={2}
                            placeholder="Valfri kommentar för just detta Jira-ärende."
                            value={perTicketBodyNotes[result.id] || ''}
                            onInput={(e) => handlePerTicketNoteInput(result.id, e)}
                            onChange={(e) => handlePerTicketNoteInput(result.id, e)}
                            style={{ width: '100%', resize: 'vertical' }}
                          />
                        )}
                      </div>

                      <div style={{ marginTop: '8px', fontSize: '13px' }}>
                        <div
                          onClick={() => setPreviewExpanded((prev) => ({ ...prev, [result.id]: !prev[result.id] }))}
                          style={{ cursor: 'pointer', color: '#005b99', userSelect: 'none' }}
                        >
                          {previewExpanded[result.id] ? '▾' : '▸'} Förhandsgranskning av Jira-ärende
                        </div>
                        {previewExpanded[result.id] && (
                          <div style={{ marginTop: '6px', background: 'var(--ids-palette-neutral-100)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '4px', padding: '8px' }}>
                            {loadingPreview ? (
                              <div style={{ transform: 'scale(0.5)', transformOrigin: 'left center', height: '24px' }}>
                                <IDSSpinner srtext="Hämtar förhandsgranskning..." />
                              </div>
                            ) : (
                              <>
                                {previewError && (
                                  <IDSAlert type="error" className="ids-mb-2">
                                    <p>{previewError}</p>
                                  </IDSAlert>
                                )}
                                <div style={{ fontWeight: 600, marginBottom: '6px' }}>{previewSummary}</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{previewBody}</div>
                                {selectedGeneralException && (
                                  <>
                                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--ids-palette-neutral-50)' }}>
                                      Generellt undantag
                                    </div>
                                    <div style={{ marginBottom: '8px' }}>
                                      {selectedGeneralException}
                                    </div>
                                  </>
                                )}
                                {selectedSimilarKeys.length > 0 && (
                                  <>
                                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--ids-palette-neutral-50)' }}>
                                      Liknande ärenden
                                    </div>
                                    <div>
                                      {selectedSimilarKeys.join(', ')}
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{ fontSize: '12px', color: 'var(--ids-palette-neutral-50)', marginBottom: '4px', fontWeight: 600 }}>
                        Se tidigare beslut:
                      </div>

                      {/* Similar tickets section */}
                      {ticketsLoading ? (
                        <div style={{ transform: 'scale(0.5)', transformOrigin: 'left center', height: '24px' }}>
                          <IDSSpinner srtext="Hämtar liknande ärenden..." />
                        </div>
                      ) : (
                        <SimilarTicketsSuggestion
                          tickets={tickets}
                          onCheckedChange={(issueKey, isChecked) => handleSimilarTicketCheck(result.id, issueKey, isChecked)}
                        />
                      )}

                      {/* General exception and TKB field rules */}
                      {(loadingSuggestions || loadingTkb) ? (
                        <div style={{ marginTop: '6px', transform: 'scale(0.5)', transformOrigin: 'left center', height: '24px' }}>
                          <IDSSpinner srtext="Hämtar förslag..." />
                        </div>
                      ) : (
                        <>
                          <GeneralExceptionSuggestion
                            suggestions={resultSuggestions}
                            onChange={(key) => handleExceptionChange(result.id, key)}
                          />
                          <TkbFieldRulesPanel
                            rules={tkbRules[result.id] || []}
                            onDismiss={(remaining) => handleTkbDismiss(result.id, remaining)}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginLeft: '16px', flexShrink: 0 }}>
                  <IDSButtonGroup unresponsive>
                    <IDSButton secondary={isSelected} size="s" onClick={() => !isSubmitting && onToggle(result.id, false)} disabled={isSubmitting}>
                      Skicka ej
                    </IDSButton>
                    <IDSButton secondary={!isSelected} size="s" onClick={() => !isSubmitting && onToggle(result.id, true)} disabled={isSubmitting}>
                      Skicka till Jira
                    </IDSButton>
                  </IDSButtonGroup>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="ids-flex ids-mt-4"
        style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--ids-palette-neutral-90)', paddingTop: '16px', gap: '1rem' }}
      >
        <IDSButton onClick={onCancel} disabled={isSubmitting}>
          Avbryt
        </IDSButton>
        <IDSButton onClick={handleConfirm} disabled={isSubmitting || selectedCount === 0}>
          {isSubmitting
            ? 'Skapar ärenden...'
            : `Skicka ${selectedCount} av ${totalCount} till Jira →`}
        </IDSButton>
      </div>
    </div>
  );
};

export default DeviationReviewCard;
